import { useEffect, useState } from 'react';
import { withLocalize } from 'react-localize-redux';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';
import { Col, Container, Nav, NavItem, Row } from 'reactstrap';

import { FlexContainerComponent } from '../components';
import Helmet from '../components/common/Helmet';
import { latinizeSanitized } from '../services/language/latinize';
import useStore from '../store/useStore';

const loadTranslations = async languageCode => {
  try {
    const translations = await import(`../assets/translations/${languageCode}.json`);
    return translations.default;
  } catch (error) {
    console.error(`Error loading translations for language code "${languageCode}":`, error);
    return null;
  }
};

const transformTranslationData = (translation, languageCode) =>
  translation &&
  Object.keys(translation)
    .map(key => translation[key])
    .filter(item => item.title)
    .map(item => ({
      ...item,
      link: `${languageCode}/help/${latinizeSanitized(item.title)}`,
      internalLink: latinizeSanitized(item.title),
    }))
    .map(item => transformNavData([item], languageCode)[0]);

const transformNavData = (data, languageCode) => {
  return data.map(item => {
    const { link, title, internalLink, ...navs } = item;
    const nav = Object.keys(navs)
      .filter(key => key.startsWith('nav'))
      .map(key => navs[key])
      .map(item => ({
        ...item,
        link: `${languageCode}/help/${internalLink}/${latinizeSanitized(item.title)}`,
        internalLink: `${internalLink}/${latinizeSanitized(item.title)}`,
      }));
    return { link, title, internalLink, nav };
  });
};

const HelpContainer = () => {
  const [translation, setTranslation] = useState(null);

  const languageCode = useStore(state => state.language);
  const { groupId, queryId } = useParams();

  useEffect(() => {
    document.body.classList.add('help');
    return () => {
      document.body.classList.remove('help');
    };
  }, []);

  useEffect(() => {
    const fetchTranslations = async () => {
      const loadedTranslations = await loadTranslations(languageCode);
      setTranslation(transformTranslationData(loadedTranslations?.HelpContainer, languageCode));
    };

    fetchTranslations();
  }, [languageCode]);

  const query =
    groupId && queryId
      ? translation
          ?.find(item => item.internalLink === groupId)
          .nav.find(item => item.internalLink === groupId + '/' + queryId)
      : null;

  return (
    <FlexContainerComponent>
      <Helmet
        titleTranslationKey="HelpContainer.title"
        descriptionTranslationKey="HelpContainer.description"
      />

      <Container>
        <Row className="mt-5 mt-sm-3 mb-4">
          <Col xs="12" sm="4" md="3">
            <Nav vertical>
              {translation?.map(item => (
                <NavItem key={item.internalLink}>
                  <NavLink key={item.link} to={`/${item.link}`}>
                    {item.title}
                  </NavLink>
                  {item.internalLink === groupId && (
                    <Nav vertical>
                      {item.nav.map(subItem => (
                        <NavItem key={subItem.internalLink}>
                          <NavLink key={subItem.link} to={`/${subItem.link}`}>
                            {subItem.title}
                          </NavLink>
                        </NavItem>
                      ))}
                    </Nav>
                  )}
                </NavItem>
              ))}
            </Nav>
          </Col>
          <Col>
            {query && (
              <div className="mt-4 mt-sm-0">
                <h1>{query.title}</h1>
                <p dangerouslySetInnerHTML={{ __html: query.text }} />
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </FlexContainerComponent>
  );
};

export default withLocalize(HelpContainer);
